// Device readiness preferences (MiroTalk-style "get ready" step). Captured on the readiness
// screen and carried into the call so mediaClient.start() opens the chosen camera/mic and
// honors the initial on/off state. Pure helpers here are unit-tested; the React component
// (DeviceReadiness.tsx) and mediaClient own the actual getUserMedia side effects.

export interface MediaDevicePreferences {
  /** Selected camera deviceId, or undefined for the browser default. */
  cameraDeviceId?: string;
  /** Selected microphone deviceId, or undefined for the browser default. */
  micDeviceId?: string;
  /** Whether the camera track should start enabled in the call. */
  cameraEnabled: boolean;
  /** Whether the microphone track should start enabled in the call. */
  micEnabled: boolean;
}

export const DEFAULT_MEDIA_PREFERENCES: MediaDevicePreferences = {
  cameraEnabled: true,
  micEnabled: true
};

export interface DeviceOption {
  deviceId: string;
  label: string;
}

/**
 * Map enumerateDevices() output to selectable options for one kind. Browsers hide labels until
 * permission is granted, so a numbered Hebrew-friendly fallback label is used when empty.
 * Devices without a deviceId (some browsers before permission) are dropped.
 */
export function toDeviceOptions(
  devices: MediaDeviceInfo[],
  kind: "videoinput" | "audioinput",
  fallbackLabel: (index: number) => string
): DeviceOption[] {
  return devices
    .filter((d) => d.kind === kind && !!d.deviceId)
    .map((d, i) => ({ deviceId: d.deviceId, label: d.label || fallbackLabel(i) }));
}

/**
 * Pick the deviceId that should be selected: keep the current one if it is still present,
 * otherwise fall back to the first available option (or undefined when there are none).
 */
export function resolveSelectedDevice(current: string | undefined, options: DeviceOption[]): string | undefined {
  if (current && options.some((o) => o.deviceId === current)) return current;
  return options[0]?.deviceId;
}

type DeviceSelection = Pick<MediaDevicePreferences, "cameraDeviceId" | "micDeviceId">;

function audioConstraint(micDeviceId?: string): MediaTrackConstraints | boolean {
  return micDeviceId ? { deviceId: { exact: micDeviceId } } : true;
}

/**
 * Return a copy of the preferences with `micEnabled` updated. Used when the mic is toggled on
 * the post-readiness waiting/directory screens so the new state carries into the call.
 */
export function withMicEnabled(prefs: MediaDevicePreferences, micEnabled: boolean): MediaDevicePreferences {
  return { ...prefs, micEnabled };
}

/** getUserMedia constraints for the readiness PREVIEW — the selected devices, default quality. */
export function buildPreviewConstraints(sel: DeviceSelection): MediaStreamConstraints {
  return {
    audio: audioConstraint(sel.micDeviceId),
    video: sel.cameraDeviceId ? { deviceId: { exact: sel.cameraDeviceId } } : true
  };
}

/**
 * Whether the readiness "continue" action may be taken: only once a preview stream has been
 * acquired successfully, and never while still acquiring or showing an error. This prevents
 * bypassing the check before camera/mic access is actually proven.
 */
export function canContinueReadiness(s: { loading: boolean; error: boolean; streamReady: boolean }): boolean {
  return !s.loading && !s.error && s.streamReady;
}

/** getUserMedia constraints for the CALL — the selected devices plus the call quality ranges. */
export function buildCallConstraints(sel: DeviceSelection): MediaStreamConstraints {
  const video: MediaTrackConstraints = {
    width: { ideal: 1280, min: 640 },
    height: { ideal: 720 },
    frameRate: { ideal: 25, min: 15 }
  };
  if (sel.cameraDeviceId) video.deviceId = { exact: sel.cameraDeviceId };
  return { audio: audioConstraint(sel.micDeviceId), video };
}
